"use client";

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { UserRound } from "lucide-react";
import { UserAvatarLink } from "modules/common/components/navigation/UserAvatarLink";
import { ClerkAvatar } from "modules/common/components/avatar/ClerkAvatar";
import { GameIcon } from "modules/common/components/Icons/GameIcon";
import { StoreChips } from "modules/users/components/StoreChips";
import ContactUserButton from "modules/chat/components/ContactUserButton";
import { useUserPreview } from "modules/users/hooks/useUserPreview";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { ActivityStatus, GameId } from "modules/common/model/models";
import { useUserActivityStatus } from "modules/users/hooks/useUserActivityStatus";
import { getGameDisplayName } from "utils/constants";
import { cn, isNewTrader } from "utils/utils";

interface UserHoverCardProps {
  userId: string;
  username: string;
  avatar: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  ring?: boolean;
  ringColor?: string;
  maxUsernameLength?: number;
  usernameClassName?: string;
  className?: string;
  activityStatus?: ActivityStatus;
  /** Resolve status client-side when the parent response is viewer-independent. */
  resolveActivityStatus?: boolean;
  children?: React.ReactNode;
}

/** How long the pointer must rest on an avatar before the card opens and its
 * data is fetched — sweeping across a grid of avatars fetches nothing. */
const OPEN_DELAY_MS = 400;
/** Grace period for crossing the gap between the avatar and the card. */
const CLOSE_DELAY_MS = 200;

/** w-80. Known up front so centering doesn't need a width measurement. */
const CARD_WIDTH_PX = 320;
/** Gap between the card and the avatar, and minimum viewport inset. */
const CARD_GAP_PX = 8;
/** Space to clear at the viewport top when deciding above vs below: the
 * sticky AppBar (h-16, z-40) plus the gap. The card's z-30 keeps it under
 * the bar, so anything opening in that band would be half-hidden. */
const TOP_INSET_PX = 72;
/** Keep game preferences informative without letting them crowd out identity. */
const MAX_VISIBLE_GAMES = 3;

export const compactCount = new Intl.NumberFormat("en", {
  notation: "compact",
});

/** One cell of the stat row. `tabular-nums` keeps digits from jittering
 *  between users; the em dash holds the column when a count didn't load. */
export function PreviewStat({
  value,
  label,
  className,
  zeroLabel,
}: {
  value?: number;
  label: string;
  className?: string;
  /** Shown instead of "0" — "New" beats a zero that reads as a red flag. */
  zeroLabel?: string;
}) {
  return (
    <div>
      <div
        className={cn(
          "text-lg font-bold tabular-nums leading-tight",
          // Zero and unknown both drop the accent so they don't outrank the
          // counts that carry something — but a zeroLabel is a word, not a
          // count, and greying it out is the discouragement it exists to avoid.
          value || (value === 0 && zeroLabel)
            ? className
            : "text-base-content/30"
        )}
      >
        {value === undefined
          ? "—"
          : value === 0 && zeroLabel
            ? zeroLabel
            : compactCount.format(value)}
      </div>
      <div className="text-base-content/50 text-xs">{label}</div>
    </div>
  );
}

/**
 * Avatar + username link that grows a preview card on hover — same portal
 * pattern as SimpleUserAvatarMenu (`createPortal` to body + fixed
 * positioning), with the card placed from the avatar's measured rect. CSS
 * anchor positioning / the popover API were tried first and reverted: Safari
 * resolves anchor rects in the scroll container's unscrolled coordinate space
 * for top-layer elements, parking every card on the first-hovered avatar.
 */
export function UserHoverCard({
  userId,
  username,
  avatar,
  size = "md",
  ring = true,
  ringColor,
  maxUsernameLength,
  usernameClassName,
  className,
  activityStatus,
  resolveActivityStatus = false,
  children,
}: UserHoverCardProps) {
  // `hover: hover` is the real condition — touch browsers apply sticky hover on
  // tap, which would pop the card open on a tablet. False during SSR, so every
  // avatar starts as a plain link (and the portal never renders server-side).
  const canHover = useMediaQuery("(min-width: 768px) and (hover: hover)");
  const [isOpen, setIsOpen] = useState(false);
  // Latched on first open so a never-hovered avatar fetches nothing. These
  // render one per cell in infinite grids.
  const [engaged, setEngaged] = useState(false);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { user, haves, wants, isLoading } = useUserPreview(userId, engaged);
  const resolvedActivityStatus = useUserActivityStatus(userId, activityStatus, {
    enabled: resolveActivityStatus,
  });

  const cardRef = useRef<HTMLDivElement>(null);

  // Held unmounted until the fetch settles so the card appears complete
  // instead of reflowing from skeletons. `isLoading` goes false on error too,
  // so a failed fetch still opens rather than hanging shut.
  const showCard = canHover && isOpen && !isLoading;

  // Centered above the avatar, anchored by `bottom` so the gap to the avatar
  // is exact whatever the card's height; flips below only when there's no
  // room above. Positioning writes styles on the DOM node directly — a scroll
  // handler that goes through setState repaints one frame behind the scroll
  // and the card visibly stutters after the avatar.
  const positionCard = useCallback(() => {
    const card = cardRef.current;
    const trigger = wrapperRef.current;
    if (!card || !trigger) return;
    const a = trigger.getBoundingClientRect();
    const left = Math.min(
      Math.max(CARD_GAP_PX, a.left + a.width / 2 - CARD_WIDTH_PX / 2),
      window.innerWidth - CARD_WIDTH_PX - CARD_GAP_PX
    );
    card.style.left = `${Math.round(left)}px`;
    if (a.top - card.offsetHeight - CARD_GAP_PX >= TOP_INSET_PX) {
      card.style.top = "auto";
      card.style.bottom = `${Math.round(window.innerHeight - a.top + CARD_GAP_PX)}px`;
    } else {
      card.style.bottom = "auto";
      card.style.top = `${Math.round(a.bottom + CARD_GAP_PX)}px`;
    }
  }, []);

  // Layout effect: the card must be placed between portal mount and first
  // paint, or it flashes at its unpositioned spot. Capture-phase scroll
  // listener because the grids scroll in an inner container, not the window.
  useLayoutEffect(() => {
    if (!showCard) return;
    positionCard();
    window.addEventListener("scroll", positionCard, true);
    window.addEventListener("resize", positionCard);
    return () => {
      window.removeEventListener("scroll", positionCard, true);
      window.removeEventListener("resize", positionCard);
    };
  }, [showCard, positionCard]);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    if (isOpen) return;
    openTimer.current = setTimeout(() => {
      setEngaged(true);
      setIsOpen(true);
    }, OPEN_DELAY_MS);
  }, [clearTimers, isOpen]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
  }, [clearTimers]);

  // Close on activation rather than waiting out CLOSE_DELAY_MS, so whatever
  // the click opens (navigation, Clerk's sign-in modal) isn't shadowed by a
  // lingering card.
  const dismiss = useCallback(() => {
    clearTimers();
    setIsOpen(false);
  }, [clearTimers]);

  useEffect(() => clearTimers, [clearTimers]);

  const linkProps = {
    username,
    avatar,
    size,
    ring,
    ringColor,
    maxUsernameLength,
    usernameClassName,
    className,
    online: resolvedActivityStatus === "active",
    children,
  };

  if (!canHover) {
    return <UserAvatarLink {...linkProps} />;
  }

  const games = user?.preferences?.games ?? [];
  const visibleGames = games.slice(0, MAX_VISIBLE_GAMES);
  const hiddenGames = games.slice(MAX_VISIBLE_GAMES);
  const stores = user?.lgs ?? [];
  const showsNewTrader =
    user?.completedTrades === 0 && isNewTrader(user?.createdAt);

  return (
    <div
      ref={wrapperRef}
      // ponytail: className lands on both the wrapper (so a call site's
      // `flex-1` sizes the flex item) and the inner link (so `gap-2.5` reaches
      // it). Split in two only if one ever needs to differ.
      className={cn("inline-flex min-w-0", className)}
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      onFocus={scheduleOpen}
      onBlur={scheduleClose}
    >
      <UserAvatarLink {...linkProps} />
      {showCard &&
        createPortal(
          // Portaled to body so the grids' `overflow-x: clip` can't clip it;
          // hidden until measured so it doesn't flash at 0,0. Its own
          // enter/leave handlers keep it open while the pointer is on it —
          // the wrapper's handlers can't cover it across the portal boundary.
          <div
            ref={cardRef}
            role="group"
            aria-label={`About ${username}`}
            // z-30: below the sticky AppBar's z-40, so the card slides under
            // the bar on scroll instead of covering it.
            className="card card-sm bg-base-100 border-base-content/20 fixed z-30 w-80 border shadow-lg"
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
          >
            <div className="card-body gap-0">
              {/* Only the things that navigate or open a modal dismiss — a
                  click on the stats or the store strip leaves the card open. */}
              <Link
                href={`/users/${username}`}
                // Named group: the portal escapes call sites' grid-item
                // `group`, but keep the scoped name so nothing regresses if
                // this ever renders inline again.
                className="group/uhc flex items-center gap-3"
                onClick={dismiss}
              >
                <ClerkAvatar
                  preferredAvatar={avatar}
                  username={username}
                  size="lg"
                  online={resolvedActivityStatus === "active"}
                  alt={`${username} avatar`}
                />
                <div className="min-w-0 flex-1">
                  <div className="card-title block min-w-0 truncate text-base font-bold group-hover/uhc:underline">
                    {username}
                  </div>
                  <div className="mt-0.5 flex min-w-0 items-center gap-2">
                    {user?.createdAt && (
                      <div className="text-base-content/50 min-w-0 truncate text-xs">
                        Member since {new Date(user.createdAt).getFullYear()}
                      </div>
                    )}
                    {games.length > 0 && (
                      <div className="text-base-content/40 ml-auto flex shrink-0 items-center gap-1.5">
                        {visibleGames.map((game) => (
                          <GameIcon
                            key={game}
                            game={game as GameId}
                            size={ICON_SIZE}
                          />
                        ))}
                        {hiddenGames.length > 0 && (
                          <span
                            className="badge badge-ghost badge-xs font-medium tabular-nums"
                            title={hiddenGames
                              .map((game) => getGameDisplayName(game as GameId))
                              .join(", ")}
                            aria-label={`${hiddenGames.length} more games`}
                          >
                            +{hiddenGames.length}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Link>

              {/* Fixed columns so the card is the same height for every user. */}
              <div className="border-base-content/10 mt-3 grid grid-cols-3 border-y py-2 text-center">
                <PreviewStat
                  value={haves}
                  label="Haves"
                  className="text-warning"
                />
                <PreviewStat
                  value={wants}
                  label="Wants"
                  className="text-secondary"
                />
                {/* Same rule as the profile: "New" only while the account is
                    young. An older account with nothing to show gets the em
                    dash — the grid's columns are fixed, so it can't go silent. */}
                <PreviewStat
                  value={
                    showsNewTrader ? 0 : user?.completedTrades || undefined
                  }
                  label={showsNewTrader ? "Trader" : "Trades"}
                  zeroLabel="New"
                />
              </div>

              {/* The preview supplies only the target's public stores.
                  StoreChips preserves the richer signed-in experience by
                  merging the viewer's existing store query state locally. */}
              {stores.length > 0 && (
                <StoreChips
                  stores={stores}
                  label="Trades at"
                  highlightShared
                  ownerId={userId}
                  size="strip"
                  className="mt-3 bg-transparent"
                />
              )}

              <div className="card-actions mt-3" onClick={dismiss}>
                <Link
                  href={`/users/${username}`}
                  className="btn btn-ghost btn-sm flex-1"
                >
                  <UserRound size={ICON_SIZE} />
                  Profile
                </Link>
                <ContactUserButton userId={userId} className="mt-0 flex-1" />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
